<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import AppShell from './components/AppShell.vue'
import GameErrorToast from './components/GameErrorToast.vue'
import FeatureUnavailable from './components/FeatureUnavailable.vue'
import LoginView from './views/LoginView.vue'
import SafetyCenterView from './views/SafetyCenterView.vue'
import { ApiError, bootstrap, restoreRememberedSession } from './core/api'
import { loadRuntimeAssets } from './core/assets'
import { t } from './core/i18n'
import { prewarmMap } from './core/mapWarmCache'
import { measureUxOnce } from './core/metrics'
import { clearRealtimeAuthorization, startRealtime } from './core/realtime'
import { navigate, router } from './core/router'
import { startSessionKeepAlive } from './core/sessionKeepAlive'
import {
  clearSession,
  forgetPersistentSessionHint,
  hasAdminPermission,
  hasPersistentSessionHint,
  restoreSessionFromPeer,
  session,
  setBootstrap,
  setSession,
} from './core/session'

const AccountView = defineAsyncComponent(
  () => import('./views/AccountView.vue'),
)
const AdminAccountDetailView = defineAsyncComponent(
  () => import('./views/AdminAccountDetailView.vue'),
)
const AdminAccountsView = defineAsyncComponent(
  () => import('./views/AdminAccountsView.vue'),
)
const AdminAuditView = defineAsyncComponent(
  () => import('./views/AdminAuditView.vue'),
)
const AdminBalancingView = defineAsyncComponent(
  () => import('./views/AdminBalancingView.vue'),
)
const AdminContentEventsView = defineAsyncComponent(
  () => import('./views/AdminContentEventsView.vue'),
)
const AdminExpertToolsView = defineAsyncComponent(
  () => import('./views/AdminExpertToolsView.vue'),
)
const AdminGameMasterView = defineAsyncComponent(
  () => import('./views/AdminGameMasterView.vue'),
)
const AdminOperationsAssetsView = defineAsyncComponent(
  () => import('./views/AdminOperationsAssetsView.vue'),
)
const AdminHubView = defineAsyncComponent(
  () => import('./views/AdminHubView.vue'),
)
const AdminTrustSafetyAppealView = defineAsyncComponent(
  () => import('./views/AdminTrustSafetyAppealView.vue'),
)
const AdminTrustSafetyAppealsView = defineAsyncComponent(
  () => import('./views/AdminTrustSafetyAppealsView.vue'),
)
const AdminTrustSafetyCaseView = defineAsyncComponent(
  () => import('./views/AdminTrustSafetyCaseView.vue'),
)
const AdminTrustSafetyView = defineAsyncComponent(
  () => import('./views/AdminTrustSafetyView.vue'),
)
const AdminWorldCreateView = defineAsyncComponent(
  () => import('./views/AdminWorldCreateView.vue'),
)
const AdminWorldTemplatesView = defineAsyncComponent(
  () => import('./views/AdminWorldTemplatesView.vue'),
)
const AdminWorldTemplateEditorView = defineAsyncComponent(
  () => import('./views/AdminWorldTemplateEditorView.vue'),
)
const AdminWorldDetailView = defineAsyncComponent(
  () => import('./views/AdminWorldDetailView.vue'),
)
const AdminWorldsView = defineAsyncComponent(
  () => import('./views/AdminWorldsView.vue'),
)
const AdminGameDesignView = defineAsyncComponent(
  () => import('./views/AdminGameDesignView.vue'),
)
const AdvancedView = defineAsyncComponent(
  () => import('./views/AdvancedView.vue'),
)
const AllianceForumView = defineAsyncComponent(
  () => import('./views/AllianceForumView.vue'),
)
const AllianceView = defineAsyncComponent(
  () => import('./views/AllianceView.vue'),
)
const ArmyView = defineAsyncComponent(() => import('./views/ArmyView.vue'))
const AttackView = defineAsyncComponent(() => import('./views/AttackView.vue'))
const BuildingModuleRedirectView = defineAsyncComponent(
  () => import('./views/BuildingModuleRedirectView.vue'),
)
const BuildingView = defineAsyncComponent(
  () => import('./views/BuildingView.vue'),
)
const BuildingsView = defineAsyncComponent(
  () => import('./views/BuildingsView.vue'),
)
const ChatView = defineAsyncComponent(() => import('./views/ChatView.vue'))
const ConstructionView = defineAsyncComponent(
  () => import('./views/ConstructionView.vue'),
)
const ContentHubView = defineAsyncComponent(
  () => import('./views/ContentHubView.vue'),
)
const DashboardView = defineAsyncComponent(
  () => import('./views/DashboardView.vue'),
)
const EspionageView = defineAsyncComponent(
  () => import('./views/EspionageView.vue'),
)
const ForumTopicView = defineAsyncComponent(
  () => import('./views/ForumTopicView.vue'),
)
const InventoryView = defineAsyncComponent(
  () => import('./views/InventoryView.vue'),
)
const loadMapView = () => import('./views/MapView.vue')
const MapView = defineAsyncComponent(loadMapView)
const MessagesView = defineAsyncComponent(
  () => import('./views/MessagesView.vue'),
)
const MovementsView = defineAsyncComponent(
  () => import('./views/MovementsView.vue'),
)
const NotFoundView = defineAsyncComponent(
  () => import('./views/NotFoundView.vue'),
)
const NotificationsView = defineAsyncComponent(
  () => import('./views/NotificationsView.vue'),
)
const RankingsView = defineAsyncComponent(
  () => import('./views/RankingsView.vue'),
)
const PublicProfileView = defineAsyncComponent(
  () => import('./views/PublicProfileView.vue'),
)
const ReportsView = defineAsyncComponent(
  () => import('./views/ReportsView.vue'),
)
const SettingsView = defineAsyncComponent(
  () => import('./views/SettingsView.vue'),
)
const SocialView = defineAsyncComponent(() => import('./views/SocialView.vue'))
const SupportView = defineAsyncComponent(
  () => import('./views/SupportView.vue'),
)
const VictoryView = defineAsyncComponent(
  () => import('./views/VictoryView.vue'),
)
const VillageView = defineAsyncComponent(
  () => import('./views/VillageView.vue'),
)
const WorldDashboardView = defineAsyncComponent(
  () => import('./views/WorldDashboardView.vue'),
)
const WorldsView = defineAsyncComponent(() => import('./views/WorldsView.vue'))

const authReady = ref(false)
const booting = ref(true)
const bootError = ref('')
let restoringToken: string | null = null
let stopSessionKeepAlive: (() => void) | null = null
let stopRealtime: (() => void) | null = null
const world = computed(
  () =>
    session.bootstrap?.worlds.find(
      (item) => item.worldId === router.current.worldId,
    ) ?? null,
)
const requiredCapability = computed<string | null>(() => {
  if (!router.current.worldId) return null
  const map: Partial<Record<typeof router.current.name, string>> = {
    'world-dashboard': 'dashboard',
    village: 'dashboard',
    dashboard: 'dashboard',
    buildings: 'buildings',
    building: 'buildings',
    construction: 'buildings',
    army: 'army',
    training: 'training',
    research: 'research',
    smithy: 'smithy',
    map: 'map',
    movements: 'movement',
    attack: 'movement',
    support: 'support',
    espionage: 'espionage',
    market: 'market',
    nobility: 'nobility',
    messages: 'messages',
    chat: 'chat',
    alliance: 'alliance',
    'alliance-forum': 'alliance',
    'forum-topic': 'alliance',
    quests: 'content',
    inventory: 'inventory',
    victory: 'endgame',
    notifications: 'notifications',
  }
  return map[router.current.name] ?? null
})
const capabilityAllowed = computed(
  () =>
    requiredCapability.value === null ||
    world.value?.capabilities[requiredCapability.value] === true,
)

function prewarmCurrentWorldMap(): void {
  const accountId = session.account?.accountId
  const currentWorld = world.value
  if (!accountId || !currentWorld || currentWorld.capabilities.map !== true)
    return
  void loadMapView()
  void prewarmMap(accountId, currentWorld.worldId)
}

async function restoreAuthenticatedSession(token: string): Promise<void> {
  if (restoringToken === token) return
  restoringToken = token
  booting.value = true
  bootError.value = ''
  try {
    const ux = await bootstrap()
    if (session.token !== token) return
    setBootstrap(ux)
    void loadRuntimeAssets()
    if (ux.restricted) navigate('/security', true)
    else if (router.current.name === 'login') navigate('/worlds', true)
  } catch (caught) {
    if (session.token !== token) return
    if (caught instanceof ApiError && caught.status === 401) {
      clearSession('expired')
    } else bootError.value = t('errors.load')
  } finally {
    if (restoringToken === token) {
      restoringToken = null
      booting.value = false
    }
  }
}

async function initializeAuthentication(): Promise<void> {
  if (!session.token) {
    const restoredFromPeer = await restoreSessionFromPeer()
    const shouldRestoreRemembered = hasPersistentSessionHint()
    if (!restoredFromPeer && shouldRestoreRemembered && !session.token) {
      try {
        setSession(await restoreRememberedSession(), true)
      } catch {
        forgetPersistentSessionHint()
        // No usable persistent session; the regular login view remains available.
      }
    }
  }

  authReady.value = true
  if (session.token) await restoreAuthenticatedSession(session.token)
  else booting.value = false
}

function restore(): void {
  if (session.token) void restoreAuthenticatedSession(session.token)
}

onMounted(() => {
  stopSessionKeepAlive = startSessionKeepAlive()
  void initializeAuthentication()
})
onBeforeUnmount(() => {
  stopSessionKeepAlive?.()
  stopRealtime?.()
})

watch(
  () => session.token,
  (token, previousToken) => {
    stopRealtime?.()
    stopRealtime = token ? startRealtime() : null
    if (!token && previousToken) void clearRealtimeAuthorization()
    if (!authReady.value) return
    if (!token) {
      booting.value = false
      bootError.value = ''
      if (previousToken && router.current.name !== 'login')
        navigate('/login', true)
      return
    }
    if (token !== previousToken || session.bootstrap === null)
      void restoreAuthenticatedSession(token)
  },
  { immediate: true },
)
watch(
  () =>
    [
      session.account?.accountId ?? '',
      router.current.worldId ?? '',
      world.value?.capabilities.map === true,
    ] as const,
  () => {
    prewarmCurrentWorldMap()
  },
  { immediate: true },
)
watch(
  () => router.current.path,
  () => {
    void nextTick(() => {
      measureUxOnce('route-shell', 'navigation:shell')
    })
  },
)
</script>

<template>
  <GameErrorToast />
  <div v-if="!authReady || booting" class="boot-screen" role="status">
    {{ t('common.loading') }}
  </div>
  <LoginView v-else-if="!session.token || router.current.name === 'login'" />
  <div v-else-if="bootError" class="boot-screen">
    <p class="error-banner">{{ bootError }}</p>
    <button type="button" @click="restore">{{ t('common.retry') }}</button>
  </div>
  <SafetyCenterView v-else-if="session.bootstrap?.restricted" />
  <AppShell v-else>
    <WorldsView v-if="router.current.name === 'worlds'" />
    <SettingsView v-else-if="router.current.name === 'settings'" />
    <AccountView v-else-if="router.current.name === 'account'" />
    <SafetyCenterView v-else-if="router.current.name === 'security-center'" />
    <PublicProfileView
      v-else-if="router.current.name === 'public-profile'"
      :key="router.current.path"
    />
    <SocialView v-else-if="router.current.name === 'social'" />
    <AdminGameDesignView
      v-else-if="router.current.name === 'admin-design' && hasAdminPermission()"
    />
    <AdminHubView
      v-else-if="router.current.name === 'admin-hub' && hasAdminPermission()"
    />
    <AdminWorldsView
      v-else-if="router.current.name === 'admin-worlds' && hasAdminPermission()"
    />
    <AdminWorldCreateView
      v-else-if="
        router.current.name === 'admin-world-create' && hasAdminPermission()
      "
    />
    <AdminWorldTemplatesView
      v-else-if="
        router.current.name === 'admin-world-templates' && hasAdminPermission()
      "
    />
    <AdminWorldTemplateEditorView
      v-else-if="
        router.current.name === 'admin-world-template-editor' &&
        hasAdminPermission()
      "
    />
    <AdminWorldDetailView
      v-else-if="
        router.current.name === 'admin-world-detail' && hasAdminPermission()
      "
    />
    <AdminAccountsView
      v-else-if="
        router.current.name === 'admin-accounts' && hasAdminPermission()
      "
    />
    <AdminAccountDetailView
      v-else-if="
        router.current.name === 'admin-account-detail' && hasAdminPermission()
      "
    />
    <AdminGameMasterView
      v-else-if="router.current.name === 'admin-gm' && hasAdminPermission()"
    />
    <AdminTrustSafetyView
      v-else-if="
        router.current.name === 'admin-trust-safety' && hasAdminPermission()
      "
    />
    <AdminTrustSafetyCaseView
      v-else-if="
        router.current.name === 'admin-trust-safety-case' &&
        hasAdminPermission()
      "
    />
    <AdminTrustSafetyAppealsView
      v-else-if="
        router.current.name === 'admin-trust-safety-appeals' &&
        hasAdminPermission()
      "
    />
    <AdminTrustSafetyAppealView
      v-else-if="
        router.current.name === 'admin-trust-safety-appeal' &&
        hasAdminPermission()
      "
    />
    <AdminAuditView
      v-else-if="router.current.name === 'admin-audit' && hasAdminPermission()"
    />
    <AdminContentEventsView
      v-else-if="
        router.current.name === 'admin-content' && hasAdminPermission()
      "
    />
    <AdminBalancingView
      v-else-if="
        router.current.name === 'admin-balance' && hasAdminPermission()
      "
    />
    <AdminOperationsAssetsView
      v-else-if="
        router.current.name === 'admin-operations' && hasAdminPermission()
      "
    />
    <AdminExpertToolsView
      v-else-if="router.current.name === 'admin-tools' && hasAdminPermission()"
    />
    <FeatureUnavailable
      v-else-if="
        [
          'admin-design',
          'admin-hub',
          'admin-worlds',
          'admin-world-create',
          'admin-world-detail',
          'admin-accounts',
          'admin-account-detail',
          'admin-gm',
          'admin-trust-safety',
          'admin-trust-safety-case',
          'admin-trust-safety-appeals',
          'admin-trust-safety-appeal',
          'admin-audit',
          'admin-content',
          'admin-balance',
          'admin-operations',
          'admin-tools',
        ].includes(router.current.name) || !capabilityAllowed
      "
    />
    <WorldDashboardView
      v-else-if="router.current.name === 'world-dashboard'"
      :key="router.current.path"
    />
    <VillageView
      v-else-if="router.current.name === 'village'"
      :key="router.current.path"
    />
    <DashboardView
      v-else-if="router.current.name === 'dashboard'"
      :key="router.current.path"
    />
    <BuildingsView
      v-else-if="router.current.name === 'buildings'"
      :key="router.current.path"
    />
    <BuildingView
      v-else-if="router.current.name === 'building'"
      :key="router.current.path"
    />
    <ConstructionView
      v-else-if="router.current.name === 'construction'"
      :key="router.current.path"
    />
    <ArmyView
      v-else-if="router.current.name === 'army'"
      :key="router.current.path"
    />
    <BuildingModuleRedirectView
      v-else-if="router.current.name === 'training'"
      :key="router.current.path"
      module="training"
    />
    <BuildingModuleRedirectView
      v-else-if="router.current.name === 'research'"
      :key="router.current.path"
      module="research"
    />
    <BuildingModuleRedirectView
      v-else-if="router.current.name === 'smithy'"
      :key="router.current.path"
      module="smithy"
    />
    <BuildingModuleRedirectView
      v-else-if="router.current.name === 'nobility'"
      :key="router.current.path"
      module="nobility"
    />
    <MapView
      v-else-if="router.current.name === 'map'"
      :key="router.current.path"
    />
    <MovementsView
      v-else-if="router.current.name === 'movements'"
      :key="router.current.path"
    />
    <AttackView
      v-else-if="router.current.name === 'attack'"
      :key="router.current.path"
    />
    <SupportView
      v-else-if="router.current.name === 'support'"
      :key="router.current.path"
    />
    <EspionageView
      v-else-if="router.current.name === 'espionage'"
      :key="router.current.path"
    />
    <BuildingModuleRedirectView
      v-else-if="router.current.name === 'market'"
      :key="router.current.path"
      module="market"
    />
    <ReportsView
      v-else-if="router.current.name === 'reports'"
      :key="router.current.path"
    />
    <MessagesView
      v-else-if="router.current.name === 'messages'"
      :key="router.current.path"
    />
    <ChatView
      v-else-if="router.current.name === 'chat'"
      :key="router.current.path"
    />
    <AllianceView
      v-else-if="router.current.name === 'alliance'"
      :key="router.current.path"
    />
    <AllianceForumView
      v-else-if="router.current.name === 'alliance-forum'"
      :key="router.current.path"
    />
    <ForumTopicView
      v-else-if="router.current.name === 'forum-topic'"
      :key="router.current.path"
    />
    <ContentHubView
      v-else-if="router.current.name === 'quests'"
      :key="router.current.path"
    />
    <InventoryView
      v-else-if="router.current.name === 'inventory'"
      :key="router.current.path"
    />
    <RankingsView
      v-else-if="router.current.name === 'rankings'"
      :key="router.current.path"
    />
    <VictoryView
      v-else-if="router.current.name === 'victory'"
      :key="router.current.path"
    />
    <NotificationsView
      v-else-if="router.current.name === 'notifications'"
      :key="router.current.path"
    />
    <AdvancedView
      v-else-if="router.current.name === 'advanced'"
      :key="router.current.path"
    />
    <NotFoundView v-else />
  </AppShell>
</template>
